/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 4/27/15.
 */

function PackageMediaAudioIngesterController(
    this: JS5Object,
    _: JS5Object,
    AssetValidationService: JS5Object,
    DynamicPropertiesService: JS5Object,
    ModalService: JS5Object,
    PackageFileUploadService: JS5Object,
    PackageMediaService: JS5Object,
    PackageUtilService: JS5Object,
    TempFileUploadService: JS5Object,
) {

    const getAcceptProfile = () => {
        return _.get(this, 'audioAsset.acceptTemplateMember.acceptProfile');
    };

    const setAudio = (virtualPath: string, isRemoteUploadEnabled: boolean) => {
        this.audioAsset.virtualPath = virtualPath;
        this.audioAsset.assetId = -1;
        if (isRemoteUploadEnabled) {
            this.audioAsset.pendingIngest = true;
        } else {
            delete this.audioAsset.pendingIngest;
        }
        PackageUtilService.editorForm.$setDirty();
    };


    //================//
    // Template Logic //
    //================//

    this.remoteUploadEnabled = () => {
        return DynamicPropertiesService.getPropValueAsBoolean('mediaTabRemoteUpload.enabled', false);
    }

    const openChangeAcceptProfileConfirmationModal = (acceptHandler: JS5Function, rejectHandler: JS5Function) => {
        ModalService.showMessageModal(
            {
                messageText:
                    `Are you sure you want to change the audio asset's accept profile? This will clear the asset's` +
                    ` path and require you to select a new asset.`,
                pullCancelLeft: true,
                title: 'Change Audio Accept Profile',
                windowClass: 'confirm-modal-lg',
                showOK: true
            },
            acceptHandler,
            rejectHandler);
    };

    this.onAudioAcceptTemplateMemberChanged = () => {
        const setNewAudioAcceptTemplateMember = () => {
            delete this.audioAsset.virtualPath;
            delete this.audioAsset.qualityControlNote;
            this.currentFile = null;
            this.oldSelectedAudioAcceptTemplateMember = this.audioAsset.acceptTemplateMember;
            PackageUtilService.editorForm.$setDirty();
        };
        const revertOldAudioAcceptTemplateMember = () => {
            this.audioAsset.acceptTemplateMember = this.oldSelectedAudioAcceptTemplateMember;
        };

        if (!_.size(this.audioAsset.virtualPath)) {
            setNewAudioAcceptTemplateMember();
        } else {
            openChangeAcceptProfileConfirmationModal(
                setNewAudioAcceptTemplateMember,
                revertOldAudioAcceptTemplateMember);
        }
    };

    this.onViewAssetButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openViewAssetModal(this.audioAsset);
    };

    this.onQualityControlButtonClick = ($event: JS5Object) => {
        $event.stopImmediatePropagation();
        PackageMediaService.openQualityControlModal(this.audioAsset);
    };

    this.onCurrentFileChanged = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
        setAudio(null, false);
        if (_.get(this, 'currentFile.status') === 'PASSED') {
            switch (this.currentFile.type) {
                case 'ASPERA':
                    // TODO: Test in UI when Aspera is configured
                    setAudio(this.currentFile.results[0].uploadedData, true);
                    break;
                case 'LOCAL':
                    // Display fileName (Correct virtualPath is set when media is saved)
                    PackageFileUploadService.registerLocalConfigForUpload(this.uploaderConfig);
                    setAudio(`s3:fakePath/${this.currentFile.files[0].file.name}`, false);
                    break;
                case 'REMOTE':
                    setAudio(this.currentFile.files[0].virtualPath, false);
                    break;
            }
        }
    };


    //========================//
    // Remote File Validation //
    //========================//

    this.getFileExtensions = () => {
        return _.get(getAcceptProfile(), 'fileExtensions', []);
    };

    this.validateRemoteFile = (fileSystemItem: JS5Object) => {
        let virtualPath = fileSystemItem.parent + fileSystemItem.name;
        return AssetValidationService.validateMediaRemote(virtualPath, getAcceptProfile().id);
    };


    //========================//
    // Aspera File Validation //
    //========================//

    this.validateAsperaFile = (asperaFileObject: JS5Object) => {
        return AssetValidationService.validateMediaLocal(asperaFileObject.file, getAcceptProfile())
            .then((result: JS5Object) => {
                return PackageFileUploadService.parseAsperaValidationResult(PackageMediaService.packageObject, asperaFileObject, result);
            });
    };


    //=======================//
    // Local File Validation //
    //=======================//

    this.getLocalFilePreValidationErrors = (file: File, filter: JS5Object) => {
        // Doesn't add any local file filters
    };

    this.validateLocalFile = (fileItem: JS5Object, uploaderConfig: JS5Object) => {
        fileItem.onVirtualPathSet = (virtualPath: string) => {
            setAudio(virtualPath, true);
        };
        return AssetValidationService.validateMediaLocal(fileItem._file, getAcceptProfile());
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.acceptTemplateMembers = PackageMediaService.packageMediaData.acceptTemplateMembers;
        this.audioRoles = PackageMediaService.packageMediaData.audioRoles;
        this.languages = PackageMediaService.packageMediaData.languages;

        this.oldSelectedAudioAcceptTemplateMember = this.audioAsset.acceptTemplateMember;
    };

    this.initializeLocalUploaderConfig = (uploaderConfig: JS5Object) => {
        TempFileUploadService.initialize(uploaderConfig);
        this.uploaderConfig = uploaderConfig;
    };

    this.$onChanges = (changes: JS5Object) => {
        if (changes.audioAsset && this.uploaderConfig) {
            this.currentFile = null;
        }
    };

    this.$onDestroy = () => {
        PackageFileUploadService.unregisterLocalConfig(this.uploaderConfig);
    };
}

angular.module('prism-ui.package').component('packageMediaAudioAssetSelector', {
    controller: PackageMediaAudioIngesterController,
    templateUrl: 'package/editor/components/view/media/assetSelector/audio/package-media-audio-asset-selector-template.html',
    bindings: {
        audioAsset: '<',
        listIndex: '<'
    }
});
